import styled from "styled-components";
import { Link } from "react-router-dom";

export const MenuContainer = styled.div<{
  $isScrolled: boolean;
  $suppressTransition: boolean;
}>`
  --menu-height: 72px;
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--menu-height);
  box-sizing: border-box;
  padding: ${({ theme }) => theme.padding.small}
    ${({ theme }) => theme.padding.medium};
  justify-content: space-between;
  position: sticky;
  left: 0;
  top: 0;
  z-index: 100;
  isolation: isolate;
  background-color: ${({ $isScrolled, theme }) =>
    $isScrolled ? theme.colors.background : "transparent"};
  transition: ${({ $suppressTransition }) =>
    $suppressTransition
      ? "none"
      : "background-color 0.35s ease, height 0.3s ease, padding 0.3s ease"};

  @media (min-width: 1025px) {
    height: ${({ $isScrolled }) => ($isScrolled ? "66px" : "72px")};
    padding-top: ${({ $isScrolled, theme }) =>
      $isScrolled ? "6px" : theme.padding.small};
    padding-bottom: ${({ $isScrolled, theme }) =>
      $isScrolled ? "6px" : theme.padding.small};
  }

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.padding.small}
      ${({ theme }) => theme.padding.regular};
  }
  @media (max-width: 520px) {
    --menu-height: 60px;
    padding: ${({ theme }) => theme.padding.small};
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }

  @media (min-width: 2500px) {
    --menu-height: 112px;
    height: var(--menu-height);
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3500px) {
    padding: ${({ theme }) => theme.padding.regular} 1.5dvw;
  }
  @media (min-width: 3800px) {
    padding: ${({ theme }) => theme.padding.medium} 1.5dvw;
    --menu-height: 172px;
  }
  @media (min-width: 5000px) {
    --menu-height: 192px;
  }
`;

export const LogoLink = styled(Link)<{
  $compact?: boolean;
  $suppressTransition: boolean;
}>`
  display: flex;
  gap: 0;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};

  img {
    height: ${({ $compact }) => ($compact ? "40px" : "52px")};
    transition: ${({ $suppressTransition }) =>
      $suppressTransition ? "none" : "height 0.3s ease"};
  }

  @media (max-width: 1024px) {
    img {
      height: 48px;
    }
  }
  @media (max-width: 520px) {
    img {
      height: 32px;
    }
  }
  @media (min-width: 2500px) {
    img {
      height: 64px;
    }
  }
  @media (min-width: 3500px) {
    img {
      height: 72px;
    }
  }
  @media (min-width: 3800px) {
    img {
      height: 92px;
    }
  }
  @media (min-width: 4500px) {
    img {
      height: 102px;
    }
  }
  @media (min-width: 5000px) {
    img {
      height: 112px;
    }
  }
`;

export const StyledMenu = styled.menu<{
  $isScrolled: boolean;
  $suppressTransition: boolean;
}>`
  display: flex;
  margin: 0;
  background-color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.padding.xsmall};
  border-radius: 100px;
  position: absolute;
  left: 50%;
  top: ${({ $isScrolled }) => ($isScrolled ? "6px" : "28px")};
  transform: translateX(-50%);
  transition: ${({ $suppressTransition }) =>
    $suppressTransition ? "none" : "top 0.3s ease"};

  @media (max-width: 1024px) {
    display: none;
  }

  @media (min-width: 2500px) {
    top: auto;
  }
`;

export const NavLink = styled.span`
  display: flex;
  padding: ${({ theme }) => theme.padding.small}
    ${({ theme }) => theme.padding.medium};
  border-radius: 100px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.typography.medium.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  transition: color 0.4s, background-color 0.4s;
  cursor: pointer;
  text-wrap: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkBlue};
    color: ${({ theme }) => theme.colors.white};
  }

  &:focus {
    background-color: ${({ theme }) => theme.colors.darkBlue};
    color: ${({ theme }) => theme.colors.white};
    border: none;
  }

  @media (min-width: 2500px) {
    font-size: 1.5rem;
    padding: 15px ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3000px) {
    font-size: 1.75rem;
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3800px) {
    font-size: 2.25rem;
    padding: ${({ theme }) => theme.padding.medium}
      ${({ theme }) => theme.padding.xlarge};
  }
  @media (min-width: 4500px) {
    font-size: 2.5rem;
    padding: ${({ theme }) => theme.padding.medium}
      ${({ theme }) => theme.padding.xlarge};
  }
  @media (min-width: 5000px) {
    font-size: 2.75rem;
  }
`;

export const BurgerButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  position: relative;
  width: 44px;
  height: 44px;
  padding: 10px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;

  span {
    position: absolute;
    left: 10px;
    width: 24px;
    height: 2px;
    border-radius: 2px;
    background-color: currentColor;
    transform-origin: center;
    transition: top 0.25s ease, transform 0.3s ease, opacity 0.2s ease,
      background-color 0.25s ease;
  }

  span:nth-child(1) {
    top: ${({ $isOpen }) => ($isOpen ? "21px" : "14px")};
    transform: rotate(${({ $isOpen }) => ($isOpen ? "45deg" : "0deg")});
  }

  span:nth-child(2) {
    top: 21px;
    opacity: ${({ $isOpen }) => ($isOpen ? 0 : 1)};
    transform: scaleX(${({ $isOpen }) => ($isOpen ? 0 : 1)});
  }

  span:nth-child(3) {
    top: ${({ $isOpen }) => ($isOpen ? "21px" : "28px")};
    transform: rotate(${({ $isOpen }) => ($isOpen ? "-45deg" : "0deg")});
  }

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }

  @media (max-width: 1024px) {
    display: flex;
  }
`;

export const MobileMenuPanel = styled.aside<{ $isOpen: boolean }>`
  position: fixed;
  top: 72px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 90;
  display: flex;
  width: 100%;
  height: calc(100dvh - 72px);
  box-sizing: border-box;
  flex-direction: column;
  padding: ${({ theme }) => theme.padding.large}
    ${({ theme }) => theme.padding.medium};
  background: ${({ theme }) => theme.colors.background};
  overflow-y: auto;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transform: translate3d(0, ${({ $isOpen }) => ($isOpen ? "0" : "-100%")}, 0);
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease,
    visibility 0s linear ${({ $isOpen }) => ($isOpen ? "0s" : "0.45s")};
  will-change: transform, opacity;

  @media (min-width: 1025px) {
    display: none;
  }

  @media (max-width: 520px) {
    top: 60px;
    height: calc(100dvh - 60px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }
`;

export const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};

  ${NavLink} {
    width: 100%;
    box-sizing: border-box;
    padding: ${({ theme }) => theme.padding.regular} 0;
    border-radius: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey};
    font-size: clamp(1.4rem, 6vw, 2rem);

    &:hover,
    &:focus {
      border-radius: 16px;
      padding-left: ${({ theme }) => theme.padding.regular};
    }
  }
`;
